import * as THREE from "three";
import { Builder, CAR_TIERS, MATS } from "@catworld/engine";

/**
 * The car parked on the middle driveway.
 *
 * It used to be the street's one real model -- a vendored Tesla Model 3
 * .glb that sat behind a Sketchfab login, which meant a fresh clone could
 * never build the shipped game (docs/ASSETS.md tells that story and its
 * ending).  Now it is what everything else on the street already was: boxes.
 * One box per collider tier of spec.js's CAR_TIERS, the same array road.js
 * stands the colliders on, so the car the player sees and the car the cat
 * climbs are one shape by construction and can never drift.
 *
 * Every tier is drawn ground-up at full height, exactly as it collides; the
 * union of the stack IS the staircase silhouette the tiers were traced to.
 * The boxes go through the level's own Builder -- worldUV, the per-piece
 * shade, merged batches, the street's MATS palette -- so the car is built in
 * the same visual language as the houses and kerbs beside it, just owned by
 * the client instead of the level.  `solid: false` on every block because
 * collision is road.js's half of the bargain; drawing is ours.
 *
 * Paint follows the tiers' `part` tags: charcoal body, the smoked-glass
 * canopy, and along the sill band a visual-only split into bumper / wheel /
 * rocker runs so the wheels read dark under the body.  Those split points
 * are paintwork, not shape -- they subdivide the sill tier along z without
 * moving its envelope by a millimetre.
 */

/** Where the tyres are along the sill tier, z from the car's centre.  A
    Model 3's axles sit ~1.44 m either side of centre and its tyres are
    ~0.67 m across; rounded to the tier grid's two decimals. */
const WHEELS = [
  [-1.78, -1.10],   // front axle (nose is -z)
  [ 1.10,  1.78],   // rear axle
];

/**
 * Build the car and park it.
 *
 * `where` is spec.js's CAR.  The tier offsets are already in the street's
 * axes -- nose toward the garage at -z -- so parking is a translate and
 * nothing more.  Synchronous, no asset fetch: the whole car is ~20 boxes
 * of engine data, which is the point.
 *
 * @param {{x: number, y: number, z: number}} where
 */
export function buildCar(where) {
  const b = new Builder().setOwner("car");

  /** One visual box on a tier's footprint, at the tier's full ground-up
      height.  tint 0 keeps a car-uniform finish (the per-piece tint lottery
      is for fence panels, not body panels); the ground-fade AO stays.
      @param {number} z0  @param {number} z1  @param {number} top
      @param {number} w   @param {THREE.Material} mat */
  const box = (z0, z1, top, w, mat) =>
    b.block(where.x, 0, where.z + (z0 + z1) / 2, w, top, z1 - z0, mat,
            { solid: false, tint: 0, tile: 0 });

  for (const { z0, z1, top, w, part } of CAR_TIERS) {
    if (part === "sill") {
      // The one tier that gets painted in runs: body-colour bumpers and
      // rockers, tyre-dark bands where the wheels are.
      const cuts = [z0, ...WHEELS.flat(), z1];
      for (let i = 0; i + 1 < cuts.length; i++) {
        box(cuts[i], cuts[i + 1], top, w,
            i % 2 ? MATS.tyre : MATS.carPaint);
      }
    } else {
      box(z0, z1, top, w, part === "glass" ? MATS.carGlass : MATS.carPaint);
    }
  }

  const root = new THREE.Group();
  b.finish(root);
  root.position.y = where.y;    // onto the driveway slab, like the colliders' 0
  return root;
}
